import { useFormContext, Controller } from 'react-hook-form';
import { FormHelperText } from '../../base-components';
import UploadFile from './UploadFile'; //TODO: Fix
import UploadAvatar from './UploadAvatar';
import UploadImage from './UploadImage';
import { IUploadProps } from './FormUploadFile.interfaces';
import { UploadFileTypes } from '../../../common';
import { useCallback } from 'react';

interface Props extends Omit<IUploadProps, 'file'> {
    name: string;
    type: UploadFileTypes;
    multiple?: boolean;
    enableEditor?: boolean;
    initialAspectRatio?: number;
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
}

const FormUploadFile = (props: Props) => {
    const { 
        name, 
        type, 
        multiple, 
        helperText, 
        enableEditor = true,
        initialAspectRatio,
        maxWidth,
        maxHeight,
        quality,
        ...other 
    } = props;
    const { control, setValue } = useFormContext();

    const handleDrop = useCallback(
        (acceptedFiles: File[]) => {
            const file = acceptedFiles[0];

            const newFile = Object.assign(file, {
                preview: URL.createObjectURL(file),
            });

            if (file) {
                setValue(name, newFile, {
                    shouldValidate: true,
                });
            }
        },
        [name, setValue]
    );

    const handleDelete = useCallback(() => {
        setValue(name, null, {
            shouldValidate: true,
        });
    }, [name, setValue]);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <>
                    {type === UploadFileTypes.file && (
                        <>
                            {multiple ? (
                                <UploadFile
                                    multiple
                                    accept={{
                                        'application/pdf': [],
                                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                                            [], // .docx
                                    }}
                                    files={field.value}
                                    error={!!error}
                                    helperText={
                                        (!!error || helperText) && (
                                            <FormHelperText
                                                error={!!error}
                                                sx={{ px: 2 }}
                                            >
                                                {error
                                                    ? error?.message
                                                    : helperText}
                                            </FormHelperText>
                                        )
                                    }
                                    onDrop={handleDrop}
                                    onDelete={handleDelete}
                                    {...other}
                                />
                            ) : (
                                <UploadFile
                                    accept={{
                                        'application/pdf': [],
                                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                                            [], // .docx
                                    }}
                                    file={field.value}
                                    error={!!error}
                                    helperText={
                                        (!!error || helperText) && (
                                            <FormHelperText
                                                error={!!error}
                                                sx={{ px: 2 }}
                                            >
                                                {error
                                                    ? error?.message
                                                    : helperText}
                                            </FormHelperText>
                                        )
                                    }
                                    onDrop={handleDrop}
                                    onDelete={handleDelete}
                                    {...other}
                                />
                            )}
                        </>
                    )}
                    {type === UploadFileTypes.avatar && (
                        <>
                            <UploadAvatar
                                accept={{
                                    'image/*': [],
                                }}
                                error={!!error}
                                file={field.value}
                                onDrop={handleDrop}
                                onDelete={handleDelete}
                                {...other}
                            />

                            {!!error && (
                                <FormHelperText
                                    error
                                    sx={{ px: 2, textAlign: 'center' }}
                                >
                                    {error.message}
                                </FormHelperText>
                            )}
                        </>
                    )}
                    {type === UploadFileTypes.image && (
                        <>
                            <UploadImage
                                accept={{
                                    'image/*': [],
                                    'application/pdf': [],
                                }}
                                error={!!error}
                                file={field.value}
                                onDrop={handleDrop}
                                onDelete={handleDelete}
                                enableEditor={enableEditor}
                                initialAspectRatio={initialAspectRatio}
                                maxWidth={maxWidth}
                                maxHeight={maxHeight}
                                quality={quality}
                                {...other}
                            />

                            {(!!error || helperText) && (
                                <FormHelperText
                                    error={!!error}
                                    sx={{ px: 2, textAlign: 'center', mt: 1 }}
                                >
                                    {error ? error.message : helperText}
                                </FormHelperText>
                            )}
                        </>
                    )}
                </>
            )}
        />
    );
};

export default FormUploadFile;
